import FlipToBackRounded from '@mui/icons-material/FlipToBackRounded';
import FlipToFrontRounded from '@mui/icons-material/FlipToFrontRounded';
import { Stack, Typography } from '@mui/material';
import { FC } from 'react';

import { Workflow } from '@apiContract';
import { ContainerDatePicker } from '@components/ContainerDatePicker';
import { ExistingContainerWarning } from '@components/ExistingContainerWarning';
import { SelectMailCarrier } from '@components/SelectMailCarrier';
import { SelectStorageType } from '@components/SelectStorageType';

import InputStorageGroupName from '../Inputs/InputStorageGroupName';
import { ToggleButtonGroup, ToggleButtonGroupConfig } from './components/ToggleButtonGroup';
import { SortingOrder, StorageGroupProps } from './types';
import { useStorageGroup } from './useStorageGroup';

export * from './types';
const sortingOrderOptions: ToggleButtonGroupConfig<SortingOrder>[] = [
  {
    value: SortingOrder.addToFront,
    label: 'Add to front',
    Icon: FlipToFrontRounded,
  },
  {
    value: SortingOrder.addToBack,
    label: 'Add to back',
    Icon: FlipToBackRounded,
  },
];
const TOOLTIP = 'Sorting order cannot be changed after storage group is created';

const StorageGroup: FC<StorageGroupProps> = ({
  workflow,
  sx = {},
  containerTypes,
  direction = 'row',
  defaultValue,
  onChange,
  onLoading,
  isLoading,
  isNextOrderNo,
  onContainerFound,
}) => {
  const {
    containerTypeId,
    date,
    containerNumber,
    existingContainer,
    sortingOrder,
    fetchingExistingContainer,
    onChangeValue,
  } = useStorageGroup({
    defaultValue,
    containerTypes,
    workflow,
    onChange,
    onLoading,
    isLoading,
    isNextOrderNo,
    onContainerFound,
    withIncludes: true,
  });

  return (
    <Stack sx={sx} spacing={3}>
      <Stack spacing={3} direction={{ xs: 'column', md: direction }}>
        {workflow === Workflow.DateReceived && (
          <>
            <ContainerDatePicker value={date} error={!date} fullWidth onChange={onChangeValue('date')} />
            <SelectStorageType
              workflow={workflow}
              containerTypeId={containerTypeId}
              setContainerTypeId={onChangeValue('containerTypeId')}
            />
          </>
        )}
        {(workflow === Workflow.DateReceived || workflow === Workflow.Location) && (
          <InputStorageGroupName
            defaultValue={containerNumber}
            onChange={onChangeValue('containerNumber')}
            workflow={workflow}
            containerTypeId={containerTypeId}
          />
        )}
        {workflow === Workflow.Carrier && (
          <SelectMailCarrier carrier={containerNumber} setCarrier={onChangeValue('containerNumber')} />
        )}
      </Stack>

      {existingContainer && <ExistingContainerWarning />}

      {workflow === Workflow.DateReceived && (
        <Stack spacing={2}>
          <Typography variant="subtitle2">Sorting order</Typography>
          <ToggleButtonGroup
            config={sortingOrderOptions}
            value={sortingOrder}
            tooltip={existingContainer ? TOOLTIP : ''}
            disabled={fetchingExistingContainer || !!existingContainer}
            onChange={onChangeValue('sortingOrder')}
          />
        </Stack>
      )}
    </Stack>
  );
};

export default StorageGroup;
